/**
 * App-wide auth state.
 *
 * On mount it asks the API which auth mode it's actually in (`GET /auth/mode`)
 * and adopts it as the authoritative mode — so the SPA can never drift out of
 * sync with the backend (e.g. a build baked as "disabled" against a "simple"
 * API). Until that answer arrives we report `isLoading`, so the guard shows a
 * spinner rather than wrongly treating the user as signed-in or signed-out.
 *
 * After the mode is known it resolves the user from the cookie (or the synthetic
 * dev admin in disabled mode) and re-checks on tab focus.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  getAuthMode,
  getCurrentUser,
  isAuthDisabled,
  logout as doLogout,
  redirectToLogin,
  setRuntimeAuthMode,
  type AuthMode,
  type AuthUser,
} from "@/lib/auth";
import { api } from "@/lib/api-client";
import { AuthContext, type AuthContextValue } from "./context";

function isAuthMode(v: unknown): v is AuthMode {
  return v === "disabled" || v === "simple" || v === "auth0" || v === "skyagent";
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  // Block guard decisions until we've confirmed the API's mode (once per load).
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(() => {
    setUser(getCurrentUser());
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await api.get<{ mode: AuthMode }>("/api/v1/auth/mode");
        if (!cancelled && isAuthMode(res?.mode)) setRuntimeAuthMode(res.mode);
      } catch {
        // Couldn't reach /auth/mode — fall back to the build-time hint so the app
        // still functions (getAuthMode() returns it when no runtime mode is set).
      } finally {
        if (!cancelled) {
          setUser(getCurrentUser());
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isAuthDisabled()) return;
    // The cookie may change in another tab/app — re-read it on focus.
    const onFocus = () => setUser(getCurrentUser());
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [isLoading]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isLoading,
      mode: getAuthMode(),
      login: (returnPath?: string) => redirectToLogin(returnPath),
      logout: () => doLogout(),
      refresh,
    }),
    [user, isLoading, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
